import { useEffect, useState } from "react";
import { AccountBalanceHistoryClient } from "../../utilities/backend/client";
import {
  Box,
  CircularProgress,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
} from "@mui/material";
import { ExportButton, StyledTableCell } from "../../styles/StyledComponents";

const AccountHistoryAdmin = () => {
  const accountHistoryClient = new AccountBalanceHistoryClient();
  const [accountHistories, setAccountHistories] = useState<any[]>();
  const [filterAccountHistories, seFilterAccountHistories] = useState<any[]>();
  const [sortDirection, setSortDirection] = useState<"asc" | "desc">("desc");
  const [sortField, setSortField] = useState<"investmentName" | "changeDate">(
    "changeDate"
  );

  const [loading, setLoading] = useState<boolean>(true);
  const [isExporting, setIsExporting] = useState<boolean>(false);

  useEffect(() => {
    setLoading(true);
    accountHistoryClient
      .accountBalanceHistory_GetAll()
      .then(async (response) => {
        var data = JSON.parse(await response.data.text());
        setAccountHistories(data);
        setLoading(false);
      })
      .catch((error) => {
        console.log(error);
        setLoading(false);
      });
  }, []);

  useEffect(() => {
    if (accountHistories) {
      const sortedAccountHistories = [...accountHistories].sort((a, b) => {
        if (sortField === "investmentName") {
          const investmentNameA = a.investmentName ?? "";
          const investmentNameB = b.investmentName ?? "";

          const comparisonResult =
            investmentNameA.localeCompare(investmentNameB);
          return sortDirection === "asc" ? comparisonResult : -comparisonResult;
        } else if (sortField === "changeDate") {
          const dateA = new Date(a.changeDate!).getTime();
          const dateB = new Date(b.changeDate!).getTime();
          return sortDirection === "asc" ? dateA - dateB : dateB - dateA;
        }

        return 0;
      });

      seFilterAccountHistories(sortedAccountHistories);
    }
  }, [sortDirection, sortField, accountHistories]);

  const handleExportAll = () => {
    setIsExporting(true);
    accountHistoryClient
      .exportAccountBalanceHistories()
      .then(() => {
        console.log("Export successful");
      })
      .catch((error) => {
        console.error("Error exporting users", error);
      })
      .finally(() => {
        setIsExporting(false);
      });
  };

  const toggleSortDirection = (field: "investmentName" | "changeDate") => {
    setSortField((prevField) => {
      if (prevField === field) {
        setSortDirection((prevDirection) =>
          prevDirection === "asc" ? "desc" : "asc"
        );
      } else {
        setSortField(field);
        setSortDirection("asc");
      }
      return field;
    });
  };

  return (
    <>
      {loading ? (
        <Box sx={{ margin: "0 auto" }}>
          <CircularProgress />
        </Box>
      ) : (
        <div
          style={{
            display: "grid",
            gap: "10px",
          }}
        >
          <ExportButton
            disabled={isExporting}
            variant="contained"
            color="primary"
            onClick={handleExportAll}
          >
            Export All
          </ExportButton>
          <TableContainer component={Paper}>
            <Table>
              <TableHead>
                <TableRow>
                  <StyledTableCell>User Name</StyledTableCell>
                  <StyledTableCell
                    onClick={() => toggleSortDirection("changeDate")}
                    sx={{
                      cursor: "pointer",
                    }}
                  >
                    {sortDirection === "asc" ? (
                      <span>&uarr;</span>
                    ) : (
                      <span>&darr;</span>
                    )}{" "}
                    Change Date
                  </StyledTableCell>
                  <StyledTableCell
                    onClick={() => toggleSortDirection("investmentName")}
                    sx={{
                      cursor: "pointer",
                    }}
                  >
                    {sortDirection === "asc" ? (
                      <span>&uarr;</span>
                    ) : (
                      <span>&darr;</span>
                    )}{" "}
                    Investment Name
                  </StyledTableCell>
                  <StyledTableCell>Payment Type</StyledTableCell>
                  <StyledTableCell>Old Value</StyledTableCell>
                  <StyledTableCell>New Value</StyledTableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {filterAccountHistories ? (
                  filterAccountHistories.map((accountHistory) => (
                    <TableRow key={accountHistory.id}>
                      <TableCell sx={{ maxWidth: "200px" }}>
                        {accountHistory.userName}
                      </TableCell>
                      <TableCell sx={{ minWidth: "85px" }}>
                        {accountHistory.changeDate
                          ? new Date(
                              accountHistory.changeDate
                            ).toLocaleDateString("en-US")
                          : ""}
                      </TableCell>
                      <TableCell>{accountHistory.investmentName}</TableCell>
                      <TableCell>{accountHistory.paymentType}</TableCell>
                      <TableCell>{accountHistory.oldValue}</TableCell>
                      <TableCell>{accountHistory.newValue}</TableCell>
                    </TableRow>
                  ))
                ) : (
                  <TableRow></TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </div>
      )}
    </>
  );
};

export default AccountHistoryAdmin;
